import {
    Label,
    makeStyles,
    mergeClasses,
    Radio,
    RadioGroup,
    RadioGroupOnChangeData,
    Switch,
    SwitchOnChangeData,
    typographyStyles,
    useId,
} from '@fluentui/react-components';
import * as React from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import { FeatureFlagName } from '../../../constants/features';
import { setUserSettings } from '../../../redux/actions/user-settings/user-settings-action-creator';
import { useStackStyles } from '../../../themes/styles/flexbox-styles';
import { ThemeMode } from '../../../themes/themes';
import { ReturnVoid } from '../../../types/return-void';
import { UserSettings } from '../../../types/user-settings';
import { isFeatureFlagEnabled } from '../../../utilities/features';

const messages = defineMessages({
    themeModeCheckboxAriaLabel: {
        id: 'SettingsFormFieldGroup_ThemeModeCheckbox_AriaLabel',
        defaultMessage: 'Change theme mode',
        description: 'Aria label for "change theme mode" checkbox',
    },
    multiMonitorCheckboxAriaLabel: {
        id: 'SettingsFormFieldGroup_MultiMonitorCheckbox_AriaLabel',
        defaultMessage: 'Enable Multiple Monitor Support',
        description: 'Aria label for "Enable Multi Monitor Support" checkbox',
    },
    useWindowsAppCheckboxAriaLabel: {
        id: 'SettingsFormFieldGroup_UseWindowsAppCheckbox_AriaLabel',
        defaultMessage: 'Enable use windows Application for remote connection',
        description: 'Aria label for "use windows app" checkbox',
    },
    connectViaWindowsAppButtonLabelText: {
        id: 'SettingsFormFieldGroup_ConnectViaWindowsAppButtonLabel_Text',
        defaultMessage: 'Connect via {appName}',
        description: '{appName} should not be localized',
    },
    connectViaRemoteDesktopLegacyButtonLabelText: {
        id: 'SettingsFormFieldGroup_ConnectViaRemoteDesktopLegacyButtonLabel_Text',
        defaultMessage: 'Connect via {appName} (legacy)',
        description: '{appName} should not be localized',
    },
    useSingleMonitorText: {
        id: 'SettingsFormFieldGroup_UseSingleMonitor_Text',
        defaultMessage: 'Use a single monitor',
        description: 'Label for choice group option "Use a single monitor"',
    },
    useMultiMonitorText: {
        id: 'SettingsFormFieldGroup_UseMultiMonitor_Text',
        defaultMessage: 'Use multiple monitors',
        description: 'Label for choice group option "Use a multiple monitors"',
    },
    windowsAppNameText: {
        id: 'SettingsFormFieldGroup_WindowsAppName_Text',
        defaultMessage: 'Windows App',
        description: 'Label for choice group option "Windows App"',
    },
    remoteDesktopAppNameText: {
        id: 'SettingsFormFieldGroup_RemoteDesktopAppName_Text',
        defaultMessage: 'Remote Desktop',
        description: 'Label for choice group option "Remote Desktop"',
    },
});

/**
 * Styles
 */

const useHeaderStyles = makeStyles({
    root: { ...typographyStyles.subtitle2 },
});

const useFieldGroupStyles = makeStyles({
    root: {
        gap: '12px',
    },
});

const useScreenReaderOnlyStyles = makeStyles({
    root: {
        position: 'absolute',
        width: '1px',
        height: '1px',
        padding: 0,
        margin: '-1px',
        overflow: 'hidden',
        clip: 'rect(0, 0, 0, 0)',
        whiteSpace: 'nowrap',
        border: 0,
    },
});

/**
 * END Styles
 */

type UserSettingsPanelComponentProps = {
    currentUserSettings: UserSettings;
    submit: ReturnVoid<typeof setUserSettings>;
};

export const UserSettingsFormFieldGroup: React.FC<UserSettingsPanelComponentProps> = (
    props: UserSettingsPanelComponentProps
) => {
    const { currentUserSettings, submit } = props;

    // Styles
    const stackStyles = useStackStyles();
    const headerStyles = useHeaderStyles();
    const fieldGroupStyles = useFieldGroupStyles();
    const screenReaderOnlyStyles = useScreenReaderOnlyStyles();

    // Intl hooks
    const { formatMessage } = useIntl();
    const displayConfigurationLabelId = useId('display-configuration-label');

    const monitorOptions: JSX.Element[] = React.useMemo(
        () => [
            <Radio key="false" value="false" label={formatMessage(messages.useSingleMonitorText)} />,
            <Radio key="true" value="true" label={formatMessage(messages.useMultiMonitorText)} />,
        ],
        [formatMessage]
    );

    const onMultiMonitorChange = React.useCallback(
        (_ev: React.FormEvent<HTMLDivElement>, data: RadioGroupOnChangeData) => {
            const selectedKey = data.value === 'true';
            const newSettings: UserSettings = {
                ...currentUserSettings,
                useMultiMonitor: selectedKey,
            };
            submit(newSettings);
        },
        [submit, currentUserSettings]
    );

    const onThemeModeChange = React.useCallback(
        (_ev: React.ChangeEvent<HTMLInputElement>, data: SwitchOnChangeData) => {
            const newSettings: UserSettings = {
                ...currentUserSettings,
                themeMode: data.checked ? ThemeMode.DarkMode : ThemeMode.LightMode,
            };
            submit(newSettings);
        },
        [submit, currentUserSettings]
    );

    return (
        <div className={mergeClasses(stackStyles.root, fieldGroupStyles.root)}>
            {isFeatureFlagEnabled(FeatureFlagName.EnableThemeModeToggle) && (
                <div className={stackStyles.item}>
                    <div className={stackStyles.root}>
                        <Label>
                            <FormattedMessage
                                id="SettingsFormFieldGroup_ThemeModeCheckbox_Label"
                                defaultMessage="Dark mode"
                                description='Label for "theme mode checkbox" checkbox'
                            />
                        </Label>
                        <Switch
                            aria-label={formatMessage(messages.themeModeCheckboxAriaLabel)}
                            role="checkbox"
                            onChange={onThemeModeChange}
                            defaultChecked={currentUserSettings?.themeMode === ThemeMode.DarkMode}
                            labelPosition="above"
                        />
                    </div>
                </div>
            )}
            <h3 className={mergeClasses(stackStyles.item, headerStyles.root)}>
                <FormattedMessage
                    id="SettingsFormFieldGroup_DevBoxSectionHeader"
                    defaultMessage="Dev box"
                    description="Header for the section with dev box specific settings"
                />
            </h3>
            <div className={stackStyles.item}>
                <Label aria-hidden="true">
                    <FormattedMessage
                        id="SettingsFormFieldGroup_MultiMonitorPlaneCheckbox_Label"
                        defaultMessage="Display configuration"
                        description='Label for "Enable multi monitor support" checkbox'
                    />
                </Label>
                <span id={displayConfigurationLabelId} className={screenReaderOnlyStyles.root}>
                    <FormattedMessage
                        id="SettingsFormFieldGroup_MultiMonitorPlaneCheckbox_Label"
                        defaultMessage="Display configuration"
                        description='Label for "Enable multi monitor support" checkbox'
                    />
                </span>
                <RadioGroup
                    aria-labelledby={displayConfigurationLabelId}
                    onChange={onMultiMonitorChange}
                    value={currentUserSettings.useMultiMonitor.toString()}
                >
                    {monitorOptions}
                </RadioGroup>
            </div>
        </div>
    );
};
